import { useState, useEffect, lazy, ComponentType } from "react";
import { Lazy } from "../Lazy/Lazy";
import { signOut } from "../../services/signOut";
import { storeService } from "../../services/store.service";
import type { PerksPageProps } from "./PerksLanding";

const PerksLanding = lazy(() => import("./PerksLanding"));
const PerkDetail = lazy(() => import("./PerkDetail"));
const PerkUse = lazy(() => import("./PerkUse"));
const PerksHistory = lazy(() => import("./PerksHistory"));
const PerkMemberCard = lazy(() => import("./PerkMemberCard"));

const pages: Record<string, ComponentType<PerksPageProps>> = {
  landing: PerksLanding,
  detail: PerkDetail,
  use: PerkUse,
  history: PerksHistory,
  member: PerkMemberCard,
};

/**
 * Route shell for MPG Perks: hydrates the signed-in user, the company logo
 * and the selected store from local storage, exactly as the packages
 * wrapper does, so the pages render inside the same chrome.
 */
export default function PerksWrapper({ page }: { page: keyof typeof pages }) {
  const [user, setUser] = useState<any | null>(null);
  const [selectedStore, setSelectedStore] = useState<any | null>(null);
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    const initData = async () => {
      const { ensureCMSUser } = await import("../../services/auth.service");
      const cmsUser = await ensureCMSUser();
      if (cmsUser) {
        setUser({
          name: `${cmsUser.firstName || ""} ${cmsUser.lastName || ""}`.trim(),
          email: cmsUser.email || "",
          picture: cmsUser.picture || "",
        });
      }

      let logoUrl = storeService.getCompanyLogoUrl();
      if (!logoUrl) {
        try {
          await storeService.getStores();
          logoUrl = storeService.getCompanyLogoUrl();
        } catch (error) {
          console.error("Error fetching company logo:", error);
        }
      }
      if (logoUrl) setCompanyLogoUrl(logoUrl);

      const storedStore = localStorage.getItem("selectedStore");
      if (storedStore) {
        try {
          setSelectedStore(JSON.parse(storedStore));
        } catch (e) {
          console.error("Error parsing stored store:", e);
        }
      }
    };

    initData();
  }, []);

  const Page = pages[page];

  return (
    <Lazy>
      <Page
        user={user}
        onLogin={(profile) => {
          setUser(profile);
          localStorage.setItem("cms_user", JSON.stringify(profile));
        }}
        onLogout={() => {
          signOut();
          setUser(null);
        }}
        onLoginError={(error) => console.error("Login error:", error)}
        selectedStore={selectedStore}
        onLocationSelect={(store) => {
          setSelectedStore(store);
          localStorage.setItem("selectedStore", JSON.stringify(store));
        }}
        companyLogoUrl={companyLogoUrl}
      />
    </Lazy>
  );
}
